import axios from "axios";
import { useState, useEffect } from "react";
import "./AssignSaturday.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const getSaturdays = (month, year) => {
  const saturdays = [];
  // Use UTC to avoid timezone issues
  const date = new Date(Date.UTC(year, month - 1, 1));

  // Find first Saturday
  while (date.getUTCDay() !== 6) {
    date.setUTCDate(date.getUTCDate() + 1);
  }

  let count = 0;
  while (date.getUTCMonth() === month - 1) {
    count++;

    // Create date string in YYYY-MM-DD format using UTC
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // Create display label
    const displayDate = new Date(date);
    const label = displayDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    saturdays.push({
      date: new Date(date),
      dateStr: dateStr,
      number: count,
      label: label,
      isFifth: count === 5,
    });

    date.setUTCDate(date.getUTCDate() + 7);
  }
  return saturdays;
}

const AssignSaturday = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const empName = storedUser.name || "";
  const empId = storedUser.employeeId || "";
  const isAdmin = storedUser.role === 4;
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [saturdays, setSaturdays] = useState([]);
  const [offDays, setOffDays] = useState({});
  const [saved, setSaved] = useState(false);
  const [escalatedEmployees, setEscalatedEmployees] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // State for yearly recurring assignment modal
  const [showYearlyModal, setShowYearlyModal] = useState(false);
  const [yearlySelections, setYearlySelections] = useState({});
  const [isApplyingYearly, setIsApplyingYearly] = useState(false);

  // Show toast message
  const showToast = (message, type = 'success', duration = 3000) => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage(null);
    }, duration);
  };

  // Initialize Saturdays when month/year changes
  useEffect(() => {
    const sats = getSaturdays(month, year);
    setSaturdays(sats);
  }, [month, year]);

  // Initialize offDays when Saturdays or employees change
  useEffect(() => {
    if (saturdays.length > 0 && escalatedEmployees.length > 0) {
      const init = {};
      escalatedEmployees.forEach(m => {
        init[m.id] = {};
        saturdays.forEach(s => {
          init[m.id][s.dateStr] = false;
        });
      });
      setOffDays(init);
      setSaved(false);
      setSaveError(null);
      setInitialLoadDone(false);
    }
  }, [saturdays, escalatedEmployees]);

  // Load employees
  useEffect(() => {
    if (!empId) return;
    axios
      .get(`${API_URL}/api/reports-by-id`, { params: { empId } })
      .then((res) => {
        setEscalatedEmployees(res.data || []);
      })
      .catch((err) => {
        console.error("Error loading dropdown:", err);
        setEscalatedEmployees([]);
      });
  }, [empId]);

  // Load existing assignments after initialization
  useEffect(() => {
    if (escalatedEmployees.length > 0 && saturdays.length > 0 && !initialLoadDone) {
      loadExistingAssignments();
    }
  }, [escalatedEmployees, saturdays, initialLoadDone]);

  const loadExistingAssignments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/saturday-roster/assignments`, {
        params: { year, month }
      });

      const assignments = response.data;
      console.log('Loaded assignments:', assignments);

      if (assignments.length > 0) {
        setOffDays(prev => {
          const newOffDays = { ...prev };
          let hasUpdates = false;

          assignments.forEach(assignment => {
            if (assignment.offDates && assignment.offDates.length > 0) {
              assignment.offDates.forEach(dateStr => {
                if (newOffDays[assignment.employeeId]) {
                  newOffDays[assignment.employeeId][dateStr] = true;
                  hasUpdates = true;
                }
              });
            }
          });

          return hasUpdates ? newOffDays : prev;
        });

        const totalOffDays = assignments.reduce((sum, a) => sum + (a.offDates?.length || 0), 0);

        if (totalOffDays > 0) {
          showToast(
            `📋 Loaded existing roster for ${MONTHS[month - 1]} ${year} (${totalOffDays} off days)`,
            'info',
            4000
          );
        }
      } else {
        showToast(
          `ℹ️ No existing roster found for ${MONTHS[month - 1]} ${year}`,
          'info',
          3000
        );
      }
    } catch (error) {
      console.error("Error loading assignments:", error);
      if (error.response?.status !== 404) {
        showToast('❌ Failed to load existing assignments', 'error', 4000);
      }
    } finally {
      setIsLoading(false);
      setInitialLoadDone(true);
    }
  };

  const getOffCount = (memberId) =>
    saturdays.filter(s => !s.isFifth && offDays[memberId]?.[s.dateStr]).length;

  const toggleOff = (memberId, sat) => {
    if (sat.isFifth) return;
    const isOff = offDays[memberId]?.[sat.dateStr];
    const offCount = getOffCount(memberId);
    if (!isOff && offCount >= 2) return;
    setOffDays(prev => ({
      ...prev,
      [memberId]: { ...prev[memberId], [sat.dateStr]: !isOff }
    }));
    setSaved(false);
    setSaveError(null);
  };

  const saveAssignments = async () => {
    setIsSaving(true);
    setSaveError(null);

    const assignments = escalatedEmployees.map(member => {
      const offDates = saturdays
        .filter(sat => !sat.isFifth && offDays[member.id]?.[sat.dateStr])
        .map(sat => sat.dateStr);

      return {
        employeeId: member.id,
        employeeName: member.name,
        offDates
      };
    });

    const hasAssignments = assignments.some(a => a.offDates.length > 0);

    try {
      const response = await axios.post(`${API_URL}/api/saturday-roster/save`, {
        assignments,
        year,
        month
      });

      setSaved(true);

      if (hasAssignments) {
        const totalOffDays = assignments.reduce((sum, a) => sum + a.offDates.length, 0);
        showToast(
          `✅ Roster saved for ${MONTHS[month - 1]} ${year} (${totalOffDays} off days)`,
          'success',
          4000
        );
      } else {
        showToast(
          `✅ Cleared all assignments for ${MONTHS[month - 1]} ${year}`,
          'success',
          4000
        );
      }

      setTimeout(() => setSaved(false), 3000);

    } catch (error) {
      console.error("Error saving assignments:", error);
      setSaveError(error.response?.data?.message || "Failed to save assignments");
      showToast('❌ Failed to save assignments', 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  };

  // Initialize yearly selections for modal (based on Saturday positions)
  const initializeYearlySelections = () => {
    const selections = {};
    escalatedEmployees.forEach(employee => {
      selections[employee.id] = {
        sat1: false,
        sat2: false,
        sat3: false,
        sat4: false
      };
    });
    setYearlySelections(selections);
  };

  // Open yearly modal
  const openYearlyModal = () => {
    if (escalatedEmployees.length === 0) {
      showToast('No employees found', 'error', 3000);
      return;
    }
    initializeYearlySelections();
    setShowYearlyModal(true);
  };

  // Handle yearly selection toggle (based on Saturday position)
  const toggleYearlySelection = (employeeId, satPosition) => {
    setYearlySelections(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [satPosition]: !prev[employeeId][satPosition]
      }
    }));
  };

  // Get selected count for an employee in yearly modal
  const getYearlySelectedCount = (employeeId) => {
    const selections = yearlySelections[employeeId] || {};
    return Object.values(selections).filter(val => val === true).length;
  };

  // Apply yearly recurring assignments to all months from current month onward
  const applyYearlyAssignments = async () => {
    setIsApplyingYearly(true);

    try {
      // Only apply to employees who have exactly 2 selections; skip those with 0
      // Reject if any employee has exactly 1 selection (incomplete)
      const employeesWithPartial = escalatedEmployees.filter(employee => {
        const selectedCount = getYearlySelectedCount(employee.id);
        return selectedCount === 1;
      });

      if (employeesWithPartial.length > 0) {
        const names = employeesWithPartial.map(e => e.name).join(', ');
        showToast(`Please select exactly 2 Saturday positions for: ${names}`, 'error', 5000);
        setIsApplyingYearly(false);
        return;
      }

      // Only include employees who have 2 selections
      const selectedEmployees = escalatedEmployees.filter(employee =>
        getYearlySelectedCount(employee.id) === 2
      );

      if (selectedEmployees.length === 0) {
        showToast('Please select 2 Saturday positions for at least one employee', 'error', 4000);
        setIsApplyingYearly(false);
        return;
      }

      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();

      // Apply only from current month to December (past months are locked)
      const remainingMonths = [];
      for (let m = currentMonth; m <= 12; m++) {
        remainingMonths.push({ month: m, year: currentYear });
      }

      let totalApplied = 0;
      let failedMonths = [];

      // Apply to each month
      for (const { month: targetMonth, year: targetYear } of remainingMonths) {
        try {
          // Get Saturdays for this month
          const monthSaturdays = getSaturdays(targetMonth, targetYear);

          // Filter out 5th Saturdays (only positions 1-4)
          const validSaturdays = monthSaturdays.filter(sat => !sat.isFifth);

          // Create assignments only for employees who have exactly 2 selections
          const assignments = selectedEmployees.map(employee => {
            const selections = yearlySelections[employee.id];
            const offDates = [];

            // Map Saturday positions to actual dates
            // sat1 = 1st Saturday (index 0), sat2 = 2nd Saturday (index 1), etc.
            if (selections.sat1 && validSaturdays[0]) {
              offDates.push(validSaturdays[0].dateStr);
            }
            if (selections.sat2 && validSaturdays[1]) {
              offDates.push(validSaturdays[1].dateStr);
            }
            if (selections.sat3 && validSaturdays[2]) {
              offDates.push(validSaturdays[2].dateStr);
            }
            if (selections.sat4 && validSaturdays[3]) {
              offDates.push(validSaturdays[3].dateStr);
            }

            return {
              employeeId: employee.id,
              employeeName: employee.name,
              offDates
            };
          });

          // Save assignments for this month
          await axios.post(`${API_URL}/api/saturday-roster/save`, {
            assignments,
            year: targetYear,
            month: targetMonth
          });

          totalApplied++;

          // If this is the current month being viewed, reload the data
          if (targetMonth === month && targetYear === year) {
            await loadExistingAssignments();
          }

        } catch (error) {
          console.error(`Failed to apply for ${MONTHS[targetMonth - 1]} ${targetYear}:`, error);
          failedMonths.push(`${MONTHS[targetMonth - 1]} ${targetYear}`);
        }
      }

      // Show success message
      if (failedMonths.length === 0) {
        showToast(
          `✅ Applied yearly recurring assignments for ${selectedEmployees.length} employee(s) across ${totalApplied} months (${MONTHS[currentMonth - 1]} to December ${currentYear})`,
          'success',
          5000
        );
      } else {
        showToast(
          `⚠️ Applied to ${totalApplied} months. Failed: ${failedMonths.join(', ')}`,
          'warning',
          5000
        );
      }

      // Close modal
      setShowYearlyModal(false);

    } catch (error) {
      console.error("Error applying yearly assignments:", error);
      showToast('❌ Failed to apply yearly recurring assignments', 'error', 4000);
    } finally {
      setIsApplyingYearly(false);
    }
  };

  const allValid = escalatedEmployees.every(m => getOffCount(m.id) === 2);
  const fifthSat = saturdays.find(s => s.isFifth);
  const isSingleMember = escalatedEmployees.length === 1;
  const isPastMonth = !isAdmin && (year < today.getFullYear() || (year === today.getFullYear() && month < today.getMonth() + 1));

  return (
    <div className="assign-saturday-container">
      <div>

        {/* Header with Yearly Recurring Icon */}
        <div className="header-section">
          <div className="header-title-row">
            <p className="instruction-text">
              Mark <strong>exactly 2 off Saturdays</strong> per employee &nbsp;·&nbsp;
              Remaining Saturdays will be working &nbsp;·&nbsp;
              <span className="fifth-note">5th Saturday = compulsory working</span>
            </p>
            <button
              style={{
              padding: "10px",
              background: "#4f46e5",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.2s",
              letterSpacing: "0.3px",
            }}
              onClick={openYearlyModal}
              title="Set yearly Saturday"
            >
             📅 Set Yearly Saturday
            </button>
          </div>
        </div>

        {/* Month / Year */}
        <div className="controls-section">
          <select className="select-ctrl" value={month} onChange={e => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="select-ctrl" value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <div className="legend-wrapper">
            {[
              { color: "#ef4444", label: "Off (checked)" },
              { color: "#4338ca", label: "5th · Compulsory" },
              { color: "#cbd5e1", label: "Working (unchecked)" },
            ].map(l => (
              <div key={l.label} className="legend-item">
                <div className="legend-color" style={{ background: l.color }} />
                {l.label}
              </div>
            ))}
          </div>

          {isLoading && (
            <div className="loading-spinner">Loading...</div>
          )}
        </div>

        {/* Single Member Warning Banner */}
        {isSingleMember && (
          <div className="warning-banner">
            <span className="banner-icon">⚠️</span>
            <strong>Single team member detected</strong> — Assignment is disabled. Need at least 2 members to create Saturday roster.
          </div>
        )}

        {/* 5th Saturday banner */}
        {fifthSat && !isSingleMember && (
          <div className="fifth-banner">
            <span className="banner-icon">🔒</span>
            <strong>{fifthSat.label}</strong> is the 5th Saturday — compulsory working for all. No off option.
          </div>
        )}

        {/* Past month read-only banner */}
        {isPastMonth && (
          <div className="warning-banner">
            <span className="banner-icon">🔒</span>
            <strong>{MONTHS[month - 1]} {year} is a past month</strong> — View only. Editing is not allowed for past months.
          </div>
        )}

        {/* Error banner */}
        {saveError && (
          <div className="error-banner">
            <span className="banner-icon">❌</span>
            <strong>Error:</strong> {saveError}
          </div>
        )}

        {/* Table */}
        <div className="table-wrapper">
          <div className="table-scroll">
            <table className="sat-table">
              <thead>
                <tr>
                  <th className="th-name">EMPLOYEE</th>
                  {saturdays.map(sat => {
                    const offCount = escalatedEmployees.filter(m => offDays[m.id]?.[sat.dateStr]).length;
                    return (
                      <th key={sat.dateStr} className={`th-sat ${sat.isFifth ? "head-fifth" : ""}`}>
                        <span className="sat-date">{sat.label}</span>
                        {sat.isFifth
                          ? <span className="sat-badge fifth-badge">5th · All</span>
                          : <span className="sat-badge off-badge">Off</span>
                        }
                        <span className="col-count">
                          {sat.isFifth ? `All working` : `${offCount} off`}
                        </span>
                      </th>
                    );
                  })}
                  <th className="th-total">OFF COUNT</th>
                </tr>
              </thead>

              <tbody>
                {escalatedEmployees.map(member => {
                  const offCount = getOffCount(member.id);
                  const isMaxed = offCount >= 2;

                  return (
                    <tr key={member.id} className="tr-body">
                      <td className="td-name">
                        <div className="employee-name-cell">
                          <span className="name-text">{member.name}</span>
                        </div>
                      </td>

                      {saturdays.map(sat => {
                        const isOff = offDays[member.id]?.[sat.dateStr];
                        const isDisabled = !sat.isFifth && !isOff && isMaxed;

                        let cellCls = "td-cell ";
                        if (sat.isFifth) cellCls += "cell-fifth";
                        else if (isPastMonth) cellCls += isOff ? "cell-is-off" : "cell-disabled";
                        else if (isOff) cellCls += "cell-is-off";
                        else if (isDisabled) cellCls += "cell-disabled";
                        else if (isSingleMember) cellCls += "cell-disabled";
                        else cellCls += "cell-available";

                        return (
                          <td
                            key={sat.dateStr}
                            className={cellCls}
                            onClick={() => !isSingleMember && !isPastMonth && toggleOff(member.id, sat)}
                            title={
                              isPastMonth ? "Past month — view only" :
                                isSingleMember ? "Cannot assign when only one member exists" :
                                  sat.isFifth ? "5th Saturday — compulsory, cannot be off" :
                                    isDisabled ? "Already selected 2 off days" : ""
                            }
                          >
                            {sat.isFifth && (
                              <div className="cb indigo">
                                <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                  <path d="M1 4.5l3 3 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                            )}

                            {!sat.isFifth && (
                              <div className={`cb ${isOff ? "red" : isDisabled || isSingleMember || isPastMonth ? "ghost" : ""}`}>
                                {isOff && (
                                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                    <path d="M1 4.5l3 3 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}

                      <td className="td-cell count-cell">
                        <div className="off-count-cell">
                          <span className={`row-badge ${offCount === 2 ? "badge-ok" : "badge-warn"}`}>
                            {offCount}/2 {offCount === 2 ? "✓" : ""}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                <tr className="summary-row">
                  <td className="name-cell">Off Count</td>
                  {saturdays.map(sat => {
                    const count = escalatedEmployees.filter(m => offDays[m.id]?.[sat.dateStr]).length;
                    return (
                      <td key={sat.dateStr} className={`summary-cell ${sat.isFifth ? "summary-fifth" : "summary-off"}`}>
                        {sat.isFifth ? "—" : count > 0 ? count : "—"}
                      </td>
                    );
                  })}
                  <td className={`summary-total ${allValid ? "summary-valid" : "summary-invalid"}`}>
                    {allValid ? "✓ Done" : `${escalatedEmployees.filter(m => getOffCount(m.id) === 2).length}/${escalatedEmployees.length}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Save Section */}
        <div className="save-section">
          {!allValid && !isSingleMember && (
            <span className="warning-message">
              ⚠ {escalatedEmployees.filter(m => getOffCount(m.id) < 2).length} member(s) need exactly 2 off Saturdays
            </span>
          )}

          {!isSingleMember && !isPastMonth && (
            <button
              className={`save-btn ${saved ? "saved" : ""} ${isSaving ? "saving" : ""}`}
              disabled={isSaving}
              onClick={saveAssignments}
              title={isSaving ? "Saving..." : ""}
            >
              {isSaving ? "Saving..." : saved ? "✓ Saved" : "Save Assignments"}
            </button>
          )}
        </div>
      </div>

      {/* Yearly Recurring Assignment Modal - Based on Saturday Positions */}
      {showYearlyModal && (
        <div className="modal-overlay" onClick={() => !isApplyingYearly && setShowYearlyModal(false)}>
          <div className="modal-content yearly-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Set Yearly Recurring Saturday Preferences</h2>
              <button
                className="modal-close"
                onClick={() => !isApplyingYearly && setShowYearlyModal(false)}
                disabled={isApplyingYearly}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Select <strong>exactly 2 Saturday positions</strong> per employee (1st, 2nd, 3rd, or 4th Saturday).<br />
                Only employees with 2 selections will be updated — you can leave others blank to skip them.<br />
                These preferences will be applied to <strong>{MONTHS[today.getMonth()]}</strong> through <strong>December {today.getFullYear()}</strong>.
                <br /><span className="note-text">⚠️ Past months (before {MONTHS[today.getMonth()]}) will NOT be affected.</span>
              </p>

              <div className="recurring-table-wrapper">
                <div className="recurring-table-scroll">
                  <table className="sat-table yearly-sat-table">
                    <thead>
                      <tr>
                        <th className="th-name">EMPLOYEE</th>
                        <th className="th-sat">
                          <span className="sat-date">1st Saturday</span>
                          <span className="sat-badge off-badge">Off</span>
                        </th>
                        <th className="th-sat">
                          <span className="sat-date">2nd Saturday</span>
                          <span className="sat-badge off-badge">Off</span>
                        </th>
                        <th className="th-sat">
                          <span className="sat-date">3rd Saturday</span>
                          <span className="sat-badge off-badge">Off</span>
                        </th>
                        <th className="th-sat">
                          <span className="sat-date">4th Saturday</span>
                          <span className="sat-badge off-badge">Off</span>
                        </th>
                        <th className="th-total">SELECTED COUNT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {escalatedEmployees.map(employee => {
                        const selectedCount = getYearlySelectedCount(employee.id);
                        const isMaxed = selectedCount >= 2;
                        const selections = yearlySelections[employee.id] || {};

                        return (
                          <tr key={employee.id} className="tr-body">
                            <td className="td-name">
                              <div className="employee-name-cell">
                                <span className="name-text">{employee.name}</span>
                              </div>
                            </td>

                            {/* 1st Saturday */}
                            <td
                              className={`td-cell ${selections.sat1 ? "cell-is-off" : (!isMaxed ? "cell-available" : "cell-disabled")}`}
                              onClick={() => toggleYearlySelection(employee.id, 'sat1')}
                              title={!selections.sat1 && isMaxed ? "Already selected 2 Saturdays" : ""}
                            >
                              <div className={`cb ${selections.sat1 ? "red" : (!isMaxed ? "" : "ghost")}`}>
                                {selections.sat1 && (
                                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                    <path d="M1 4.5l3 3 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            </td>

                            {/* 2nd Saturday */}
                            <td
                              className={`td-cell ${selections.sat2 ? "cell-is-off" : (!isMaxed ? "cell-available" : "cell-disabled")}`}
                              onClick={() => toggleYearlySelection(employee.id, 'sat2')}
                              title={!selections.sat2 && isMaxed ? "Already selected 2 Saturdays" : ""}
                            >
                              <div className={`cb ${selections.sat2 ? "red" : (!isMaxed ? "" : "ghost")}`}>
                                {selections.sat2 && (
                                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                    <path d="M1 4.5l3 3 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            </td>

                            {/* 3rd Saturday */}
                            <td
                              className={`td-cell ${selections.sat3 ? "cell-is-off" : (!isMaxed ? "cell-available" : "cell-disabled")}`}
                              onClick={() => toggleYearlySelection(employee.id, 'sat3')}
                              title={!selections.sat3 && isMaxed ? "Already selected 2 Saturdays" : ""}
                            >
                              <div className={`cb ${selections.sat3 ? "red" : (!isMaxed ? "" : "ghost")}`}>
                                {selections.sat3 && (
                                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                    <path d="M1 4.5l3 3 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            </td>

                            {/* 4th Saturday */}
                            <td
                              className={`td-cell ${selections.sat4 ? "cell-is-off" : (!isMaxed ? "cell-available" : "cell-disabled")}`}
                              onClick={() => toggleYearlySelection(employee.id, 'sat4')}
                              title={!selections.sat4 && isMaxed ? "Already selected 2 Saturdays" : ""}
                            >
                              <div className={`cb ${selections.sat4 ? "red" : (!isMaxed ? "" : "ghost")}`}>
                                {selections.sat4 && (
                                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                    <path d="M1 4.5l3 3 6-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            </td>

                            <td className="td-cell count-cell">
                              <div className="off-count-cell">
                                <span className={`row-badge ${selectedCount === 2 ? "badge-ok" : "badge-warn"}`}>
                                  {selectedCount}/2 {selectedCount === 2 ? "✓" : ""}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn cancel-btn"
                onClick={() => setShowYearlyModal(false)}
                disabled={isApplyingYearly}
              >
                Cancel
              </button>
              <button
                className="modal-btn apply-btn"
                onClick={applyYearlyAssignments}
                disabled={isApplyingYearly}
              >
                {isApplyingYearly ? "Applying..." : "Apply to Remaining Months"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className={`toast ${toastType}`}>
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default AssignSaturday;